import React, { useEffect, useMemo, useState } from 'react';
import {
  Heart,
  Trash2,
  ShoppingBag,
  X,
  AlertCircle,
  RefreshCw,
  LogIn,
  UserPlus,
  Minus,
  Plus,
  Check,
} from 'lucide-react';
import { Product, User as UserType } from '../types';

interface WishlistProps {
  onNavigate: (page: string) => void;
  onClose?: () => void;
  onSelectProduct?: (product: Product) => void;
  onAddToCart?: (product: Product, quantity: number) => Promise<boolean>;
  user?: UserType | null;
  onWishlistCheckout?: (items: Array<{ product: Product; quantity: number }>) => void;
}

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://sparklinggems.shop/api'
    : 'http://localhost/sparkling-gems-backend-php/api';

const SITE_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://sparklinggems.shop'
    : 'http://localhost/sparkling-gems-backend-php';

const PLACEHOLDER_IMAGE =
  'https://placehold.co/80x80/f5f5f5/999999?text=No+Image';

type ProductCategory = 'jewelry' | 'bags' | 'fragrances';

interface WishlistApiItem {
  id?: number;
  wishlist_id?: number;
  product_id?: number;
  name?: string;
  product_name?: string;
  db_name?: string;
  price?: number | string;
  product_price?: number | string;
  image?: string;
  image_url?: string;
  product_image?: string;
  product_image_url?: string;
  db_image_url?: string;
  db_image?: string;
  category?: string;
  product_category?: string;
  description?: string;
  product_description?: string;
  quantity?: number | string;
  selected?: boolean | number;
  stock?: number | string | null;
  available_stock?: number | string | null;
  slug?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

interface WishlistItem {
  id: number;
  wishlist_id: number;
  name: string;
  price: number;
  image: string;
  image_url?: string;
  product_image?: string;
  product_image_url?: string;
  db_image_url?: string;
  db_image?: string;
  category: string;
  description: string;
  quantity: number;
  selected: boolean;
  stock?: number;
  slug?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

const Wishlist: React.FC<WishlistProps> = ({
  onNavigate,
  onClose,
  onSelectProduct,
  onAddToCart,
  user,
  onWishlistCheckout,
}) => {
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [isRendered, setIsRendered] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const openTimer = setTimeout(() => {
      setIsVisible(true);
    }, 20);

    return () => clearTimeout(openTimer);
  }, []);

  useEffect(() => {
    loadWishlist();
  }, [retryCount, user?.id]);

  const hasUser = useMemo(() => !!getUserId(), [user]);

  const handleClose = () => {
    setIsVisible(false);

    setTimeout(() => {
      setIsRendered(false);
      onClose?.();
    }, 300);
  };

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    const toast = document.createElement('div');
    toast.className = [
      'fixed top-24 right-4 px-6 py-3 rounded-sm shadow-lg z-[202] text-white',
      type === 'success' ? 'bg-green-600' : 'bg-red-600',
    ].join(' ');
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  function getUserId(): number | null {
    if (user?.id) return user.id;

    try {
      const userStr = localStorage.getItem('sparklingUser');
      if (userStr) {
        const parsedUser = JSON.parse(userStr);
        if (parsedUser?.id) return Number(parsedUser.id);
      }

      const fallbackUserStr = localStorage.getItem('user');
      if (fallbackUserStr) {
        const parsedUser = JSON.parse(fallbackUserStr);
        if (parsedUser?.id) return Number(parsedUser.id);
      }

      const id = localStorage.getItem('userId');
      return id ? parseInt(id, 10) : null;
    } catch {
      return null;
    }
  }

  function getGuestId(): string {
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId =
        'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
    }
    return guestId;
  }

  function updateWishlistCount(items: WishlistItem[]) {
    localStorage.setItem('wishlistCount', String(items.length));
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new Event('wishlist-updated'));
  }

  function toNumber(value: unknown, fallback = 0): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  }

  // ============================================================
  // FIXED: getCleanImageUrl - Preserves base64 and blob URLs
  // ============================================================
  function getCleanImageUrl(imageUrl?: string | null): string {
    if (!imageUrl || typeof imageUrl !== 'string') return '';

    const trimmed = imageUrl.trim();
    if (!trimmed) return '';

    // Base64 images - return as-is (these are valid image data)
    if (trimmed.startsWith('data:image/')) return trimmed;
    
    // Blob URLs - return as-is
    if (trimmed.startsWith('blob:')) return trimmed;
    
    // Absolute URLs
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    
    // Protocol-relative URLs
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    
    // Relative paths starting with slash
    if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;

    // Other relative paths
    return `${SITE_BASE_URL}/${trimmed}`;
  }

  // ============================================================
  // FIXED: resolveImageFromItem - Properly handles all image types
  // ============================================================
  function resolveImageFromItem(item: WishlistApiItem | WishlistItem): string {
    // Try all possible image sources in priority order
    const candidates = [
      (item as any).product_image_url,  // From joined products table (most reliable)
      item.db_image_url,
      item.db_image,
      item.image_url,
      item.product_image,
      item.image,
    ];

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'string') continue;
      
      const trimmed = candidate.trim();
      if (!trimmed) continue;
      
      // Base64 images - return as-is (no modification needed)
      if (trimmed.startsWith('data:image')) {
        console.log(`✅ Using base64 image (length: ${trimmed.length})`);
        return trimmed;
      }
      
      // Blob URLs - return as-is
      if (trimmed.startsWith('blob:')) {
        console.log(`✅ Using blob URL`);
        return trimmed;
      }
      
      // Absolute URLs
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        console.log(`✅ Using absolute URL: ${trimmed.substring(0, 80)}...`);
        return trimmed;
      }
      
      // Protocol-relative URLs
      if (trimmed.startsWith('//')) {
        const fullUrl = `https:${trimmed}`;
        console.log(`✅ Using protocol-relative URL: ${fullUrl.substring(0, 80)}...`);
        return fullUrl;
      }
      
      // Relative paths starting with slash
      if (trimmed.startsWith('/')) {
        const fullUrl = `${SITE_BASE_URL}${trimmed}`;
        console.log(`✅ Using relative path: ${fullUrl.substring(0, 80)}...`);
        return fullUrl;
      }
      
      // Other relative paths
      const fullUrl = `${SITE_BASE_URL}/${trimmed}`;
      console.log(`✅ Using relative path: ${fullUrl.substring(0, 80)}...`);
      return fullUrl;
    }

    console.log(`❌ No valid image found, using placeholder`);
    return PLACEHOLDER_IMAGE;
  }

  function normalizeWishlistItem(item: WishlistApiItem): WishlistItem {
    const productId = toNumber(item.product_id ?? item.id, 0);
    const wishlistId = toNumber(item.wishlist_id ?? item.id ?? item.product_id, productId);
    const resolvedImage = resolveImageFromItem(item);

    const rawStock =
      item.stock !== null && item.stock !== undefined
        ? item.stock
        : item.available_stock;

    const stockValue = rawStock !== null && rawStock !== undefined
      ? toNumber(rawStock, 0)
      : undefined;

    return {
      id: productId,
      wishlist_id: wishlistId || productId,
      name: item.name || item.product_name || item.db_name || 'Product',
      price: toNumber(item.price ?? item.product_price, 0),
      image: resolvedImage,
      image_url: getCleanImageUrl(item.image_url) || resolvedImage,
      product_image: getCleanImageUrl(item.product_image) || resolvedImage,
      product_image_url: getCleanImageUrl((item as any).product_image_url) || resolvedImage,
      db_image_url: getCleanImageUrl(item.db_image_url) || resolvedImage,
      db_image: getCleanImageUrl(item.db_image) || resolvedImage,
      category: item.category || item.product_category || '',
      description: item.description || item.product_description || '',
      quantity: Math.max(1, toNumber(item.quantity, 1)),
      selected: item.selected === true || item.selected === 1,
      stock: stockValue,
      slug: item.slug,
      created_at: item.created_at ?? null,
      updated_at: item.updated_at ?? null,
    };
  }

  async function loadWishlist() {
    setLoading(true);
    setError(null);

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const url = userId
        ? `${API_BASE_URL}/wishlist/get.php?user_id=${userId}&_=${Date.now()}`
        : `${API_BASE_URL}/wishlist/get.php?guest_id=${guestId}&_=${Date.now()}`;

      console.log('Loading wishlist from:', url);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Wishlist API response:', data);

      if (data.success) {
        const rawItems = Array.isArray(data.data) ? data.data : [];
        const normalizedItems = rawItems.map(normalizeWishlistItem);

        console.log(
          'Normalized wishlist items:',
          normalizedItems.map((i: { id: any; name: any; image: string; }) => ({
            id: i.id,
            name: i.name,
            image_type: i.image?.startsWith('data:image') ? 'base64' : 'url',
            image_preview: i.image?.substring(0, 80),
          }))
        );

        setWishlistItems(normalizedItems);
        updateWishlistCount(normalizedItems);
      } else {
        setError(data.message || data.error || 'Failed to load wishlist');
      }
    } catch (err) {
      console.error('Error loading wishlist:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  async function removeItem(productId: number) {
    setUpdating((prev) => ({ ...prev, [productId]: true }));

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const response = await fetch(`${API_BASE_URL}/wishlist/remove.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_id: productId,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to remove item');
      }

      const updatedItems = wishlistItems.filter((item) => item.id !== productId);
      setWishlistItems(updatedItems);
      updateWishlistCount(updatedItems);
      showToast(data.message || 'Removed from wishlist');
    } catch (err) {
      console.error('Error removing item:', err);
      showToast(
        err instanceof Error ? err.message : 'Failed to remove item. Please try again.',
        'error'
      );
      await loadWishlist();
    } finally {
      setUpdating((prev) => ({ ...prev, [productId]: false }));
    }
  }

  async function updateQuantity(itemId: number, delta: number) {
    const currentItem = wishlistItems.find((item) => item.id === itemId);
    if (!currentItem) return;

    const newQuantity = Math.max(1, currentItem.quantity + delta);

    if (typeof currentItem.stock === 'number' && newQuantity > currentItem.stock) {
      showToast(`Only ${currentItem.stock} left in stock`, 'error');
      return;
    }

    setUpdating((prev) => ({ ...prev, [itemId]: true }));

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const response = await fetch(`${API_BASE_URL}/wishlist/update-quantity.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_id: itemId,
          quantity: newQuantity,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        showToast(data?.message || 'Failed to update quantity', 'error');
        await loadWishlist();
        return;
      }

      setWishlistItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        )
      );
      showToast('Quantity updated', 'success');
    } catch (err) {
      console.error('Error updating quantity:', err);
      showToast('Failed to update quantity. Please try again.', 'error');
    } finally {
      setUpdating((prev) => ({ ...prev, [itemId]: false }));
    }
  }

  const toggleSelectItem = (id: number) => {
    setWishlistItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const toggleSelectAll = () => {
    const allSelected = wishlistItems.length > 0 && wishlistItems.every((item) => item.selected);
    setWishlistItems((prev) =>
      prev.map((item) => ({ ...item, selected: !allSelected }))
    );
  };

  async function bulkRemoveItems(productIds: number[]) {
    if (!productIds.length) return true;

    try {
      const userId = getUserId();
      const guestId = getGuestId();

      const response = await fetch(`${API_BASE_URL}/wishlist/remove-bulk.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          product_ids: productIds,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const updatedItems = wishlistItems.filter((item) => !productIds.includes(item.id));
        setWishlistItems(updatedItems);
        updateWishlistCount(updatedItems);
        console.log(`Removed ${productIds.length} items from wishlist`);
        return true;
      } else {
        console.error('Failed to remove items:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Error removing items from wishlist:', error);
      return false;
    }
  }

  const handleCheckout = async () => {
    const selectedItems = wishlistItems.filter((item) => item.selected && item.quantity > 0);

    if (selectedItems.length === 0) {
      showToast('Please select at least one item to checkout', 'error');
      return;
    }

    const checkoutItems = selectedItems.map((item) => ({
      product: {
        id: item.id,
        name: item.name,
        price: item.price,
        image: resolveImageFromItem(item),
        category: normalizeCategory(item.category),
        description: item.description,
        stock: item.stock ?? 0,
        condition: 'Brand New',
        weight: '',
        karat: '',
        goldType: '',
        isNew: false,
        isBestSeller: false,
      } as Product,
      quantity: item.quantity,
    }));

    if (onWishlistCheckout) {
      onWishlistCheckout(checkoutItems);
      handleClose();
      return;
    }

    const productIds = selectedItems.map((item) => item.id);
    const checkoutButton = document.querySelector('.checkout-button') as HTMLButtonElement | null;

    if (checkoutButton) {
      checkoutButton.disabled = true;
      checkoutButton.textContent = 'Processing...';
    }

    const removed = await bulkRemoveItems(productIds);

    if (!removed) {
      showToast('Warning: Items may remain in wishlist', 'error');
    }

    const checkoutData = {
      items: checkoutItems,
      source: 'wishlist',
      timestamp: Date.now(),
      product_ids: productIds,
    };

    sessionStorage.setItem('wishlistCheckout', JSON.stringify(checkoutData));

    handleClose();
    setTimeout(() => {
      onNavigate('checkout');
    }, 300);
  };

  async function handleAddItemToCart(item: WishlistItem) {
    if (!onAddToCart) {
      showToast('Add to cart is not available right now', 'error');
      return;
    }

    if (typeof item.stock === 'number' && item.quantity > item.stock) {
      showToast(`Only ${item.stock} left in stock`, 'error');
      return;
    }

    setUpdating((prev) => ({ ...prev, [item.id]: true }));

    try {
      const product = toProduct(item);
      const success = await onAddToCart(product, item.quantity);

      if (success) {
        showToast('Added to cart');
      } else {
        showToast('Failed to add to cart', 'error');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      showToast('Failed to add to cart', 'error');
    } finally {
      setUpdating((prev) => ({ ...prev, [item.id]: false }));
    }
  }

  function handleImageError(wishlistId: number) {
    console.log(`⚠️ Image failed to load for wishlist item ${wishlistId}, using placeholder`);
    setImageErrors((prev) => ({ ...prev, [wishlistId]: true }));
  }

  function getProductImage(item: WishlistItem): string {
    if (imageErrors[item.wishlist_id]) return PLACEHOLDER_IMAGE;
    return resolveImageFromItem(item);
  }

  function handleLogin() {
    handleClose();
    setTimeout(() => onNavigate('auth'), 300);
  }

  function handleRegister() {
    handleClose();
    setTimeout(() => onNavigate('register'), 300);
  }

  function normalizeCategory(category?: string): ProductCategory {
    if (category === 'jewelry' || category === 'bags' || category === 'fragrances') {
      return category;
    }
    return 'jewelry';
  }

  function toProduct(item: WishlistItem): Product {
    return {
      id: item.id,
      name: item.name,
      price: item.price,
      image: resolveImageFromItem(item),
      category: normalizeCategory(item.category),
      description: item.description,
      stock: typeof item.stock === 'number' ? item.stock : 0,
      condition: 'Brand New',
      weight: '',
      karat: '',
      goldType: '',
      isNew: false,
      isBestSeller: false,
    };
  }

  const selectedCount = wishlistItems.filter((item) => item.selected).length;
  const totalAmount = wishlistItems
    .filter((item) => item.selected)
    .reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (!isRendered) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-white md:bg-transparent">
      <div
        className={`hidden md:block absolute inset-0 bg-stone-900/70 backdrop-blur-md transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      <div
        className={`absolute right-0 top-0 h-full w-full md:w-[450px] bg-white shadow-2xl overflow-hidden flex flex-col z-[201] transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-stone-100 bg-stone-50/50">
          <div className="flex items-center gap-2">
            <Heart size={20} className="text-gold-600" />
            <h2 className="font-serif text-xl text-stone-900">
              Wishlist ({wishlistItems.length})
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-stone-400 hover:text-stone-900 transition-colors p-2"
          >
            <X size={20} />
          </button>
        </div>

        {!hasUser && (
          <div className="px-6 pt-4">
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs text-amber-800 leading-relaxed">
                You are viewing wishlist items as a guest. Login or register to keep
                them saved to your account.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleLogin}
                  className="text-xs uppercase tracking-widest bg-gold-600 text-white px-3 py-2 rounded-sm hover:bg-gold-700 transition-colors flex items-center gap-2"
                >
                  <LogIn size={14} />
                  Login
                </button>
                <button
                  onClick={handleRegister}
                  className="text-xs uppercase tracking-widest border border-stone-300 text-stone-700 px-3 py-2 rounded-sm hover:bg-stone-50 transition-colors flex items-center gap-2"
                >
                  <UserPlus size={14} />
                  Register
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm text-stone-500">Loading your wishlist...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AlertCircle className="mx-auto text-red-400 mb-4" size={40} />
              <p className="text-red-500 text-sm mb-4">{error}</p>
              <button
                onClick={() => setRetryCount((prev) => prev + 1)}
                className="flex items-center gap-2 mx-auto text-xs text-gold-600 border border-gold-600 px-4 py-2 rounded-sm hover:bg-gold-50 transition-colors"
              >
                <RefreshCw size={14} /> Try Again
              </button>
            </div>
          ) : wishlistItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-stone-50 rounded-full flex items-center justify-center mb-6 text-stone-300">
                <Heart size={30} />
              </div>
              <p className="text-stone-500 font-light mb-4">Your wishlist is empty</p>
              <button
                onClick={() => {
                  handleClose();
                  setTimeout(() => onNavigate('shop'), 300);
                }}
                className="text-xs uppercase tracking-widest text-gold-600 border-b border-gold-600 pb-1"
              >
                Browse Collections
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-stone-100">
                <button
                  onClick={toggleSelectAll}
                  className="text-xs uppercase tracking-widest text-gold-600 hover:text-gold-500 flex items-center gap-2"
                >
                  <div
                    className={`w-4 h-4 border rounded flex items-center justify-center ${
                      wishlistItems.length > 0 && wishlistItems.every((item) => item.selected)
                        ? 'bg-gold-600 border-gold-600'
                        : 'border-stone-300'
                    }`}
                  >
                    {wishlistItems.length > 0 && wishlistItems.every((item) => item.selected) && (
                      <Check size={12} className="text-white" />
                    )}
                  </div>
                  Select All
                </button>
                <span className="text-xs text-stone-500">{selectedCount} selected</span>
              </div>

              <div className="space-y-6">
                {wishlistItems.map((item) => {
                  const productImage = getProductImage(item);
                  const reachedStockLimit =
                    typeof item.stock === 'number' && item.quantity >= item.stock;

                  return (
                    <div
                      key={item.wishlist_id}
                      className={`flex gap-4 border-b border-stone-100 pb-6 ${
                        updating[item.id] ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      <div className="flex-shrink-0 pt-1">
                        <button
                          onClick={() => toggleSelectItem(item.id)}
                          className="w-5 h-5 border rounded flex items-center justify-center hover:border-gold-500 transition-colors"
                        >
                          {item.selected && <Check size={12} className="text-gold-600" />}
                        </button>
                      </div>

                      <div className="w-20 h-20 bg-stone-100 rounded-sm overflow-hidden flex-shrink-0">
                        <img
                          src={productImage}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(item.wishlist_id)}
                          loading="lazy"
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1 gap-2">
                          <h4 className="font-serif text-sm text-stone-900 line-clamp-2">
                            {item.name}
                          </h4>

                          <button
                            onClick={() => removeItem(item.id)}
                            disabled={updating[item.id]}
                            className="text-stone-400 hover:text-red-500 transition-colors disabled:opacity-30"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>

                        <p className="text-xs text-stone-500 mb-2">
                          {item.category || ''}
                        </p>

                        <div className="flex justify-between items-center mt-2 gap-3">
                          <div>
                            <span className="text-sm font-medium text-stone-900 block">
                              ₱ {Number(item.price || 0).toLocaleString()}
                            </span>
                          </div>

                          <div className="flex items-center border border-stone-200 rounded-sm">
                            <button
                              onClick={() => updateQuantity(item.id, -1)}
                              disabled={updating[item.id] || item.quantity <= 1}
                              className="p-1 px-2 hover:bg-stone-50 disabled:opacity-30 transition-colors"
                            >
                              <Minus size={10} />
                            </button>
                            <span className="px-3 text-xs font-mono min-w-[30px] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              disabled={updating[item.id] || reachedStockLimit}
                              className="p-1 px-2 hover:bg-stone-50 disabled:opacity-30 transition-colors"
                              title={reachedStockLimit ? 'Stock limit reached' : 'Increase quantity'}
                            >
                              <Plus size={10} />
                            </button>
                          </div>
                        </div>

                        {typeof item.stock === 'number' && item.stock <= 5 && item.stock > 0 && (
                          <p className="text-[10px] text-amber-600 mt-2">
                            Only {item.stock} left in stock
                          </p>
                        )}

                        {typeof item.stock === 'number' && item.stock <= 0 && (
                          <p className="text-[10px] text-red-600 mt-2">
                            Out of stock
                          </p>
                        )}

                        {onAddToCart && (
                          <button
                            onClick={() => handleAddItemToCart(item)}
                            disabled={updating[item.id] || (typeof item.stock === 'number' && item.stock <= 0)}
                            className="mt-3 text-[10px] uppercase tracking-[0.2em] border border-stone-300 text-stone-700 px-3 py-2 rounded-sm hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-40"
                          >
                            Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {wishlistItems.length > 0 && (
          <div className="p-6 bg-stone-50 border-t border-stone-200 space-y-4">
            {selectedCount > 0 && (
              <div className="flex justify-between items-center pb-2 border-b border-stone-200">
                <span className="text-xs uppercase font-bold text-stone-500">
                  Total ({selectedCount} items)
                </span>
                <span className="font-serif text-xl text-stone-900">
                  ₱ {totalAmount.toLocaleString()}
                </span>
              </div>
            )}

            <button
              onClick={handleCheckout}
              disabled={selectedCount === 0}
              className="checkout-button w-full bg-stone-900 text-white py-4 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 transition-colors shadow-lg flex items-center justify-center gap-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={14} />
              Checkout ({selectedCount})
            </button>

            <button
              onClick={() => {
                handleClose();
                setTimeout(() => onNavigate('shop'), 300);
              }}
              className="w-full border border-stone-900 text-stone-900 py-3 uppercase text-xs tracking-[0.2em] hover:bg-stone-900 hover:text-white transition-colors rounded-sm"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;